import React, { useEffect, useRef, useState } from 'react';
import { BOAPI } from '../service/UsersService';
import utils from '../utils/utils';
import ConfirmModal from './ConfirmModal';

// תמונה יכולה להיות גם קישור חיצוני מלא (http/https, מודבק ישירות בטופס)
// ולא רק נתיב /uploads/... מקומי מהעלאת קובץ - רק בנתיב מקומי צריך להוסיף
// את BaseUrl כקידומת (בדיוק כמו resolveVideoUrl בקליינט)
const fullUrl = (path) => (!path ? null : /^https?:\/\//i.test(path) ? path : `${utils.BaseUrl}${path}`);

// YYYY-MM-DD בזמן מקומי (לא toISOString - זה UTC ויכול "לגלוש" ליום הקודם/הבא
// ליד חצות, בדיוק אותה בעיה שתועדה ב-sqlDb.ts לגבי useUTC)
const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// displayDate חוזר מהשרת כ-datetime UTC (ראו הערה למעלה) - לא מותר לגזור
// ממנו YYYY-MM-DD בגזירת-מחרוזת גולמית (String(d).slice(0,10)), כי זה
// יכול "לגלוש" ליום הקודם ב-UTC+ ולמלא את שדה ה-date עם תאריך שגוי - שאם
// שומרים בלי לשים לב, מזיז בפועל את displayDate יום אחורה ב-DB
const toDateInputValue = (d) => {
  const dt = new Date(d);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
};

const emptyForm = () => ({ title: '', message: '', imageUrl: '', linkUrl: '', linkLabel: '', displayDate: todayStr(), sortOrder: '0' });

// הוספה/עריכה של הודעה/פרסומת - announcement=null סוגר, announcement={}
// פותח ביצירה, announcement={...} (רשומה קיימת) פותח בעריכה - אותו מודל
// לשני המצבים, בדיוק כמו BusinessEditModal. שגיאת שמירה מוצגת ליד כפתור
// השמירה (footer), לא למעלה. סגירה (backdrop/✕/ביטול) מזהה שינויים שלא
// נשמרו (isDirty מול snapshot שנלקח בפתיחה) ומבקשת אישור - אותה מוסכמה
// בדיוק כמו BusinessEditModal.jsx
export default function AnnouncementEditModal({ announcement, onClose, onSaved }) {
  const isEdit = !!announcement?.id;
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);

  const snapshotRef = useRef(null);

  useEffect(() => {
    if (!announcement) return;
    const initialForm = {
      title: announcement.title || '',
      message: announcement.message || '',
      imageUrl: announcement.imageUrl || '',
      linkUrl: announcement.linkUrl || '',
      linkLabel: announcement.linkLabel || '',
      displayDate: announcement.displayDate ? toDateInputValue(announcement.displayDate) : todayStr(),
      sortOrder: String(announcement.sortOrder ?? 0),
    };
    setForm(initialForm);
    setError('');
    snapshotRef.current = JSON.stringify(initialForm);
  }, [announcement]);

  if (!announcement) return null;

  const isDirty = () => snapshotRef.current != null && JSON.stringify(form) !== snapshotRef.current;

  const attemptClose = () => {
    if (saving) return;
    if (isDirty()) {
      setShowCloseConfirm(true);
      return;
    }
    onClose();
  };

  const setField = (key) => (e) => setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingImage(true);
    setError('');
    try {
      const resp = await utils.uploadFile(file, 'announcement-images');
      if (!resp?.url) throw new Error('Upload response missing url');
      setForm((prev) => ({ ...prev, imageUrl: resp.url }));
    } catch {
      setError('העלאת התמונה נכשלה, נסו שוב');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSave = async () => {
    // כותרת אופציונלית - מודעה יכולה להיות תמונה בלבד, אבל לא ריקה לגמרי
    if (!form.title.trim() && !form.message?.trim() && !form.imageUrl) {
      setError('יש להוסיף תמונה, כותרת או טקסט למודעה');
      return;
    }
    if (!form.displayDate) {
      setError('תאריך תצוגה הוא שדה חובה');
      return;
    }
    const sortOrderNum = Number(form.sortOrder);
    if (form.sortOrder !== '' && isNaN(sortOrderNum)) {
      setError('סדר תצוגה חייב להיות מספר');
      return;
    }

    setSaving(true);
    setError('');
    try {
      const params = {
        ...(isEdit ? { id: announcement.id } : {}),
        title: form.title.trim(),
        message: form.message.trim() || null,
        imageUrl: form.imageUrl.trim() || null,
        linkUrl: form.linkUrl.trim() || null,
        linkLabel: form.linkLabel.trim() || null,
        displayDate: form.displayDate,
        sortOrder: form.sortOrder === '' ? 0 : sortOrderNum,
      };
      const resp = isEdit ? await BOAPI.updateAnnouncement(params) : await BOAPI.addAnnouncement(params);
      if (resp?.[0]?.[0]?.code === 0) {
        onSaved(resp[1]?.[0]);
      } else {
        setError(resp?.[0]?.[0]?.msg || 'שמירת ההודעה נכשלה');
      }
    } catch {
      setError('שגיאה בתקשורת עם השרת');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={s.overlay} onClick={attemptClose}>
      <div style={s.card} onClick={(e) => e.stopPropagation()}>
        <div style={s.header}>
          <h3 style={s.headerTitle}>{isEdit ? `עריכת "${announcement.title}"` : 'הודעה חדשה'}</h3>
          <button style={s.closeBtn} onClick={attemptClose} disabled={saving}>✕</button>
        </div>

        <div style={s.body}>
          <div style={s.imageRow}>
            {form.imageUrl ? (
              <img src={fullUrl(form.imageUrl)} alt="" style={s.imagePreview} />
            ) : (
              <div style={s.imagePlaceholder}>📣</div>
            )}
            <div style={s.imageInputsCol}>
              <label style={s.uploadBtn}>
                {uploadingImage ? 'מעלה...' : '📷 העלאת קובץ תמונה'}
                <input type="file" accept="image/*" onChange={handleImageUpload} style={{ display: 'none' }} disabled={uploadingImage} />
              </label>
              <span style={s.orText}>או</span>
              <input
                type="text"
                value={/^https?:\/\//i.test(form.imageUrl) ? form.imageUrl : ''}
                onChange={setField('imageUrl')}
                style={s.fieldInput}
                placeholder="הדביקו קישור ישיר לתמונה (URL)"
              />
            </div>
          </div>

          <div style={s.fieldGroup}>
            <label style={s.fieldLabel}>כותרת (אופציונלי)</label>
            <input type="text" value={form.title} onChange={setField('title')} style={s.fieldInput} placeholder="לדוגמה: מזל טוב לחג!" />
          </div>

          <div style={s.fieldGroup}>
            <label style={s.fieldLabel}>תוכן ההודעה</label>
            <textarea value={form.message} onChange={setField('message')} style={{ ...s.fieldInput, minHeight: 90, resize: 'vertical' }} placeholder="טקסט ההודעה/הפרסומת" />
          </div>

          <div style={s.grid2}>
            <div style={s.fieldGroup}>
              <label style={s.fieldLabel}>קישור לכפתור (אופציונלי)</label>
              <input type="text" value={form.linkUrl} onChange={setField('linkUrl')} style={s.fieldInput} placeholder="https://..." />
            </div>
            <div style={s.fieldGroup}>
              <label style={s.fieldLabel}>טקסט הכפתור</label>
              <input type="text" value={form.linkLabel} onChange={setField('linkLabel')} style={s.fieldInput} placeholder='ברירת מחדל: "מעבר לקישור"' disabled={!form.linkUrl.trim()} />
            </div>
          </div>

          <div style={s.grid2}>
            <div style={s.fieldGroup}>
              <label style={s.fieldLabel}>תאריך תצוגה *</label>
              <input type="date" value={form.displayDate} onChange={setField('displayDate')} style={{ ...s.fieldInput, maxWidth: 180 }} />
              <span style={s.hint}>ההודעה תוצג באפליקציה רק ביום הזה</span>
            </div>
            <div style={s.fieldGroup}>
              <label style={s.fieldLabel}>סדר תצוגה</label>
              <input type="number" value={form.sortOrder} onChange={setField('sortOrder')} style={{ ...s.fieldInput, maxWidth: 120 }} />
            </div>
          </div>
        </div>

        <div style={s.footer}>
          {error && <div style={s.errorBanner}>❌ {error}</div>}
          <div style={s.footerBtns}>
            <button style={s.cancelBtn} onClick={attemptClose} disabled={saving}>ביטול</button>
            <button style={{ ...s.saveBtn, ...(saving ? s.btnDisabled : {}) }} onClick={handleSave} disabled={saving}>
              {saving ? 'שומר...' : isEdit ? 'שמירת שינויים' : 'הוספת הודעה'}
            </button>
          </div>
        </div>
      </div>

      <ConfirmModal
        visible={showCloseConfirm}
        title="שינויים שלא נשמרו"
        message="יש שינויים שלא נשמרו. לסגור בכל זאת?"
        confirmLabel="סגירה בלי לשמור"
        danger
        onConfirm={() => { setShowCloseConfirm(false); onClose(); }}
        onCancel={() => setShowCloseConfirm(false)}
      />
    </div>
  );
}

const s = {
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.6)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 200, padding: 16, direction: 'rtl',
  },
  card: {
    width: '100%', maxWidth: 560, maxHeight: '90vh', overflowY: 'auto',
    background: '#fff', borderRadius: 20, boxShadow: '0 20px 60px rgba(0,0,0,0.35)',
  },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px 22px', borderBottom: '1px solid #e3e8ee' },
  headerTitle: { margin: 0, fontSize: 17, fontWeight: 800, color: '#1a1f36' },
  closeBtn: { background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 18, color: '#697386' },
  body: { padding: 22, display: 'flex', flexDirection: 'column', gap: 16 },

  imageRow: { display: 'flex', alignItems: 'center', gap: 16 },
  imagePreview: { width: 96, height: 54, borderRadius: 10, objectFit: 'cover', border: '1px solid #e3e8ee' },
  imagePlaceholder: { width: 96, height: 54, borderRadius: 10, background: '#EEF2FF', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22 },
  imageInputsCol: { flex: 1, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' },
  uploadBtn: { padding: '9px 16px', background: '#EEF2FF', color: '#1f6feb', border: '1px solid #c7d7fb', borderRadius: 10, cursor: 'pointer', fontSize: 12.5, fontWeight: 700, whiteSpace: 'nowrap' },
  orText: { fontSize: 12, color: '#a3acb9', fontWeight: 600 },

  fieldGroup: { display: 'flex', flexDirection: 'column', gap: 5, flex: 1 },
  fieldLabel: { fontSize: 12, fontWeight: 700, color: '#4f566b' },
  fieldInput: { padding: '9px 12px', borderRadius: 10, border: '1.5px solid #e3e8ee', fontSize: 13.5, boxSizing: 'border-box', direction: 'rtl', fontFamily: 'inherit', width: '100%' },
  hint: { fontSize: 11, color: '#8792a2' },
  grid2: { display: 'flex', gap: 14, flexWrap: 'wrap' },

  footer: { padding: '14px 22px', borderTop: '1px solid #e3e8ee', display: 'flex', flexDirection: 'column', gap: 10 },
  errorBanner: { background: '#ffebee', color: '#c62828', padding: 12, borderRadius: 8, fontSize: 13 },
  footerBtns: { display: 'flex', gap: 10 },
  cancelBtn: { flex: 1, padding: '11px 16px', background: '#fff', color: '#4f566b', border: '1.5px solid #e3e8ee', borderRadius: 10, cursor: 'pointer', fontSize: 14, fontWeight: 700 },
  saveBtn: { flex: 1, padding: '11px 16px', background: '#1f6feb', color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer', fontSize: 14, fontWeight: 700 },
  btnDisabled: { opacity: 0.6, cursor: 'not-allowed' },
};
